import React, {useState} from 'react'
import styled, {css, useTheme} from 'styled-components'

import {Headline, LinkAdapter} from '@miro-site/components_utils'
import {Box, Card, Section, Text, Grid, ArrowRight} from '@miro-site/core'

interface Item {
  headline?: React.ComponentProps<typeof Headline>
  link: React.ComponentProps<typeof LinkAdapter>
  source?: React.ReactNode
}

interface ResourcesProps {
  headline?: React.ComponentProps<typeof Headline>
  items?: Item[]
}

const ArrowIcon = styled(ArrowRight).attrs({
  $styles: {
    width: [12, 15],
    height: [10, 12],
  },
})`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const ArrowContainer = styled(Box).attrs(({theme}) => {
  return {
    $styles: {
      width: theme.spaces.xl,
      height: theme.spaces.xl,
      right: theme.spaces.md,
      bottom: theme.spaces.md,
    },
  }
})`
  /* display: flex;
  align-items: center;
  justify-content: center; */

  background: ${({theme}) => theme.colors.primaryBlue};
  border-radius: 50%;
  position: absolute;
  transition: background ${({theme}) => theme.animation.transitionDuration};
  transform: rotate(-45deg);
`

const Item = styled(Card).attrs({$pb: [80]})`
  display: block;
  background: ${({theme}) => theme.colors.primaryWhite};
  height: 100%;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    ${ArrowContainer} {
      background: ${({theme}) => theme.colors.primaryBlueHover};
    }
  }
`

const ItemHeadline = styled(Headline).attrs<Item['headline']>({
  size: 'tiny',
  tag: 'h3',
})``

const SourceContainer = styled(Box)`
  & > ${Text} {
    font-family: ${props => props.theme.fonts.formular};
    font-feature-settings: 'ss03';
    color: ${props => props.theme.colors.greyish};
  }

  & + ${ItemHeadline} {
    ${props => css`
      ${props.theme.normalizeStyles({marginTop: 8})}
    `}
  }
`

const Resources: React.FC<ResourcesProps> = ({headline, items}) => {
  const theme = useTheme()
  const [focusedElement, setFocusedElement] = useState(-1)

  const handleItemClick = (href: string, e: React.MouseEvent<HTMLElement>) => {
    const selection = document.getSelection()

    if (selection?.toString()) {
      return
    }
    e.preventDefault()
    window.open(href, '_blank')
  }

  return (
    <Section data-place="Resources" type="large">
      <Box
        $styles={{margin: 'auto', position: 'relative', maxWidth: [null, 1120]}}
        $py={[0, null]}
        $px={[20, null]}
      >
        {headline && (
          <Headline
            {...headline}
            size={headline.size || 'mid'}
            tag={headline.tag || 'h2'}
            align={headline.align || 'center'}
          ></Headline>
        )}

        {items?.length && (
          <Box $mt={theme.spaces.xl}>
            <Grid columns={items.length} align="stretch">
              {items.map((item, index) => {
                let linkText = item.headline?.text || item.link?.text

                if (typeof linkText === 'string') {
                  linkText = linkText.trim()
                }

                const link = (
                  <LinkAdapter
                    {...item.link}
                    text={linkText}
                    href={item.link.href}
                    simple_link={true}
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault()}
                    onFocus={() => {
                      setFocusedElement(index)
                    }}
                    onBlur={() => setFocusedElement(-1)}
                  ></LinkAdapter>
                )

                return (
                  <Item
                    $mt={index >= 1 ? [16, null] : null}
                    key={index}
                    $isFocused={focusedElement == index}
                    onClick={e => handleItemClick(item.link.href, e)}
                  >
                    <Box $pt={theme.spaces.lg} $px={theme.spaces.lg} $pb={0}>
                      {item.source && (
                        <SourceContainer>
                          <Text as="div" $variant="p1">
                            {item.source}
                          </Text>
                        </SourceContainer>
                      )}
                      {item.headline && <ItemHeadline {...item.headline} text={link} />}
                    </Box>
                    <ArrowContainer>
                      <ArrowIcon></ArrowIcon>
                    </ArrowContainer>
                  </Item>
                )
              })}
            </Grid>
          </Box>
        )}
      </Box>
    </Section>
  )
}

export default Resources
